import React, { useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import NativeSelect from '@material-ui/core/NativeSelect';
import MaterialTable, { MTableToolbar } from 'material-table';

import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment/locale/th';
import { uniqueId, uniqBy } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';

// Utils
import exportCsv from '../../../../utils/export/Csv';
import tableIcons from '../../../../components/table/MaterialTableIcon';
import { StyledIconButton } from '../../../../components/button/Button';
import WaterLevelModal from '../../../../components/modal/Modal';
import WaterGateChart from '../../chart/WaterGateChart';

import styles from '../style';

const useStyles = makeStyles(styles);

export default function WLGateDatalists(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [wgData, setWgData] = useState([]);
  const [agencyData, setAgencyData] = useState([]);
  const [selectAgency, setSelectAgency] = useState('');
  const [modalData, setModalData] = useState({
    open: false,
    header: t('ระดับน้ำปตร'),
    subheader: null,
    content: null,
  });
  const {
    datas,
    handleSearchMarker,
    handleSetCurrentData,
    currentMode,
    currentArea,
    userIsLogin,
  } = props;
  const isExport = (userIsLogin) ? { csv: true } : userIsLogin;

  const handleAgencyChange = (event) => {
    const selectVar = event.target.value;
    const agencyFilter = (selectVar === '') ? datas : datas.filter((d) => (d.agency.id === Number(selectVar)));
    setSelectAgency(selectVar);
    setWgData(agencyFilter);
    handleSetCurrentData(agencyFilter);
  };
  const handleChart = (data) => (e) => {
    e.preventDefault();
    setModalData({
      open: true,
      header: t('กราฟระดับน้ำปตร./ฝาย'),
      subheader: null,
      content: <WaterGateChart id={data.station.id} provinceId={data.geocode.province_code} />,
    });
  };

  useEffect(() => {
    const setWaterData = async () => {
      let agency = datas.filter((item) => item.agency.agency_name.th !== undefined).map((d) => ({
        id: d.agency.id,
        name: d.agency.agency_name.th,
      }));
      agency = uniqBy(agency.sort((a, b) => a.name.localeCompare(b.name, 'th')), 'id');
      setAgencyData(agency);
      setWgData(datas);
      handleSetCurrentData(datas);
    };

    setWaterData();
  }, [datas]);

  // force modal close
  useEffect(() => {
    if (modalData.open) modalData.open = false;
  }, [modalData.open]);

  function filterData(currentData, mode, area) {
    let filterD = [];

    if (mode === 'province' && area.code !== '0') {
      filterD = wgData.filter((d) => d.geocode.province_code === area.code);
    } else if (currentMode === 'basin' && area.code !== '0') {
      filterD = wgData.filter((d) => d.basin.basin_code === parseInt(area.code, 10));
    } else {
      filterD = currentData;
    }

    return filterD;
  }

  const formatWl = (value) => (typeof value === 'undefined' || value == null ? '-' : parseFloat(value).toFixed(2));

  return (
    <>
      <Paper className={classes.root}>
        <MaterialTable
          icons={tableIcons}
          data={filterData(wgData, currentMode, currentArea)}
          columns={[
            {
              title: t('ชื่อสถานี'),
              title_export: t('ชื่อสถานี'),
              field: 'station.tele_station_name.th',
              width: 300,
              headerStyle: {
                left: 0,
                position: 'sticky',
                zIndex: 50,
              },
              cellStyle: {
                left: 0,
                position: 'sticky',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                padding: '0em',
              },
              render: (rowData) => {
                if (
                  rowData.station.tele_station_lat
                  && rowData.station.tele_station_long
                ) {
                  return (
                    <Box display="flex" style={{ backgroundColor: 'white' }}>
                      <Button
                        style={{ textAlign: 'left' }}
                        onClick={handleSearchMarker(
                          rowData.station.id,
                          rowData.station.tele_station_lat,
                          rowData.station.tele_station_long,
                        )}
                        title={`${rowData.agency.agency_name.th}\n${t('แสดงตำแหน่งบนแผนที่')}`}
                      >
                        {`${rowData.station.tele_station_name.th}`}
                      </Button>
                    </Box>
                  );
                }
                return (
                  <Button
                    onClick={handleSearchMarker(
                      rowData.id,
                      rowData.station.tele_station_lat,
                      rowData.station.tele_station_long,
                    )}
                    title={t('แสดงตำแหน่งบนแผนที่')}
                  >
                    {`${rowData.station.tele_station_name.th}`}
                  </Button>
                );
              },
            },
            {
              title: t('รหัสสถานี'),
              type: 'string',
              field: 'station.tele_station_oldcode',
              hidden: true,
              export: false,
              searchable: true,
            },
            {
              title_export: t('ตำบล'),
              type: 'string',
              field: 'geocode.tumbon_name.th',
              searchable: true,
              export: true,
              hidden: true,
            },
            {
              title_export: t('อำเภอ'),
              type: 'string',
              field: 'geocode.amphoe_name.th',
              searchable: true,
              export: true,
              hidden: true,
            },
            {
              title_export: t('จังหวัด'),
              type: 'string',
              field: 'geocode.province_name.th',
              searchable: true,
              export: false,
              hidden: true,
            },
            {
              title: t('ที่ตั้ง'),
              field: 'address',
              type: 'string',
              export: false,
              width: 300,
              render: (rowData) => (rowData.geocode.tumbon_name.th !== '' ? `ต.${rowData.geocode.tumbon_name.th} อ.${rowData.geocode.amphoe_name.th} จ.${rowData.geocode.province_name.th}` : `อ.${rowData.geocode.amphoe_name.th} จ.${rowData.geocode.province_name.th}`),
            },
            {
              title: t('ลุ่มน้ำ'),
              title_export: t('ลุ่มน้ำ'),
              type: 'string',
              field: 'basin.basin_name.th',
              width: 200,
              hidden: !userIsLogin,
            },
            {
              title: t('ตำบล'),
              title_export: t('ตำบล'),
              field: 'geocode.tumbon_name.th',
              type: 'string',
              hidden: true,
            },
            {
              title: t('อำเภอ'),
              title_export: t('อำเภอ'),
              field: 'geocode.amphoe_name.th',
              type: 'string',
              hidden: true,
            },
            {
              title: t('จังหวัด'),
              title_export: t('จังหวัด'),
              field: 'geocode.province_name.th',
              type: 'string',
              hidden: true,
            },
            {
              title: t('เวลา'),
              title_export: t('เวลา'),
              field: 'watergate_datetime_in',
              type: 'date',
              width: 80,
              render: (rowData) => `${moment(rowData.watergate_datetime_in).locale('th').format('LT')} น.`,
            },
            {
              title:
  <>
    {t('ระดับน้ำเหนือ')}
    <br />
    <Typography variant="caption" display="block">
      (
      {t('ม.รทก.')}
      )
    </Typography>
  </>,
              title_export: 'ระดับน้ำเหนือ (ม.รทก)',
              field: 'watergate_in',
              type: 'numeric',
              width: 120,
              render: (row) => `${formatWl(row.watergate_in)}`,
            },
            {
              title:
  <>
    {t('ระดับน้ำท้าย')}
    <br />
    <Typography variant="caption" display="block">
      (
      {t('ม.รทก.')}
      )
    </Typography>
  </>,
              title_export: `${t('ระดับน้ำท้าย')} (${t('ม.รทก.')})`,
              field: 'watergate_out',
              type: 'numeric',
              width: 120,

              render: (row) => `${formatWl(row.watergate_out)}`,
            },
            {
              title:
  <>
    {t('น้ำระบายสูงสุดปัจจุบัน')}
    <br />
    <Typography variant="caption" display="block">
      (
      {t('ม.รทก.')}
      )
    </Typography>
  </>,
              title_export: `${t('น้ำระบายสูงสุดปัจจุบัน')} (${t('ม.รทก.')})`,
              field: 'watergate_out',
              type: 'numeric',
              width: 200,
              render: (row) => `${formatWl(row.watergate_out)}`,
            },
            {
              title: t('หน่วยงาน'),
              title_export: t('หน่วยงาน'),
              field: 'agency.agency_name.th',
              type: 'string',
              hidden: true,
              export: true,
            },
            {
              title: t('รหัสสถานี'),
              title_export: t('รหัสสถานี'),
              field: 'station.tele_station_oldcode',
              type: 'string',
              hidden: true,
              export: true,
            },
            {
              title_export: 'Latitude',
              type: 'string',
              field: 'station.tele_station_lat',
              hidden: true,
              export: true,
            },
            {
              title_export: 'Longitude',
              type: 'string',
              field: 'station.tele_station_long',
              hidden: true,
              export: true,
            },
            {
              field: 'graph',
              type: 'numeric',
              sorting: false,
              width: 115,
              render: (rowData) => (
                <>
                  <Tooltip title={`สถานี${rowData.station.tele_station_name.th} \n${t('คลิ๊กเพื่อแสดงกราฟ')}`}>
                    <StyledIconButton onClick={handleChart(rowData)}>
                      <FontAwesomeIcon icon={faChartLine} fontSize="small" style={{ padding: 0, color: '#888' }} />
                    </StyledIconButton>
                  </Tooltip>
                </>
              ),
            },
          ]}
          localization={{
            toolbar: {
              searchPlaceholder: t('ค้นหา'),
              exportTitle: t('ดาวน์โหลดข้อมูล ระดับน้ำปตร.'),
            },
            pagination: {
              labelRowsSelect: t('แถว'),
            },
            body: { emptyDataSourceMessage: wgData.length === 0 ? <h3>{t('กำลังโหลดข้อมูล...')}</h3> : <h3>{t('ไม่มีข้อมูลตามเงื่อนไข')}</h3> },
          }}
          options={{
            // tableLayout: 'fixed',
            pageSize: 10,
            sorting: true,
            // searchFieldStyle: {
            //   width: '50%',
            //   alignSelf: { sm: 'center', md:'flex-end' },
            // },
            rowStyle: {
              wordWrap: 'break-word',
            },
            toolbar: true,
            showTitle: false,
            search: true,
            exportButton: isExport,
            exportCsv,
            cellStyle: {
              padding: 8,
            },
          }}
          components={{
            Toolbar: (props) => (
              <div>
                <Box mx={2}>
                  <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                    <Grid item xs={12} sm={8} md={6}>
                      <MTableToolbar {...props} />
                    </Grid>
                  </Grid>
                  <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                    <Grid item xs="auto" sm="auto" md="auto">
                      <FormControl className={classes.formControl}>
                        <Box fontFamily="Kanit">
                          {t('หน่วยงาน')}
                        </Box>
                      </FormControl>
                    </Grid>
                    <Grid item xs={9} sm={4} md={4}>
                      <FormControl className={classes.formControl}>
                        <NativeSelect
                          id="agency-select"
                          value={selectAgency}
                          onChange={handleAgencyChange}
                        >
                          <option value="">{t('ทุกหน่วยงาน')}</option>
                          {agencyData.map((val) => (
                            <option key={uniqueId()} value={val.id}>{val.name}</option>
                          ))}
                        </NativeSelect>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </div>
            ),
          }}
        />
        <WaterLevelModal
          open={modalData.open}
          modalHeader={modalData.header}
          modalSubHeader={modalData.subheader}
          modalContent={modalData.content}
        />
      </Paper>
    </>
  );
}

WLGateDatalists.propTypes = {
  datas: PropTypes.instanceOf(Array).isRequired,
  handleSearchMarker: PropTypes.func.isRequired,
  handleSetCurrentData: PropTypes.func.isRequired,
  currentMode: PropTypes.string.isRequired,
  currentArea: PropTypes.instanceOf(Object).isRequired,
  userIsLogin: PropTypes.bool.isRequired,
};
